import { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { BsBuilding, BsFileEarmarkText } from 'react-icons/bs';

import Alert from '#globalComponents/Alert/Alert';
import Button from '#globalComponents/Button/Button';
import SectionCard from '#globalComponents/Card/SectionCard';
import FormField from '#globalComponents/Forms/FormField';
import Textarea from '#globalComponents/Forms/Textarea';
import TextInput from '#globalComponents/Forms/TextInput';
import ImageUploadField from '#globalComponents/ImageUploadField/ImageUploadField';
import { colors } from '#styles/colors';

import { useSaveCompanyProfile } from '../../models/companyProfile/mutations';
import { useCompanyProfile } from '../../models/companyProfile/queries';

const SECTION_FIELDS = {
	company: ['name', 'email', 'street', 'postal', 'city', 'phone', 'website'],
	letter: ['logoUrl', 'registration', 'brandPrimaryColor', 'defaultFooterText'],
};

const ALL_FIELDS = [...SECTION_FIELDS.company, ...SECTION_FIELDS.letter];

const DEFAULT_BRAND_COLOR = colors.primary[500];

const toFormData = (profile) => Object.fromEntries(ALL_FIELDS.map((key) => [key, profile?.[key] || '']));

function getUpdates(formData, profile, fields) {
	const updates = {};
	for (const key of fields) {
		const trimmed = formData[key].trim();
		if (trimmed !== (profile?.[key] || '')) updates[key] = trimmed || null;
	}
	return updates;
}

const FormFooter = ({ label, loading, disabled }) => (
	<div className="mt-6 flex justify-end border-t border-bgBase-500 pt-4">
		<Button type="submit" loading={loading} disabled={disabled}>
			{label}
		</Button>
	</div>
);

FormFooter.propTypes = {
	label: PropTypes.string.isRequired,
	loading: PropTypes.bool.isRequired,
	disabled: PropTypes.bool.isRequired,
};

export const CompanySettings = () => {
	const { data: profile, isLoading, error } = useCompanyProfile();
	const saveMutation = useSaveCompanyProfile();

	const [formData, setFormData] = useState(() => toFormData(null));
	const [savingSection, setSavingSection] = useState(null);

	useEffect(() => {
		if (profile) setFormData(toFormData(profile));
	}, [profile]);

	const handleInput = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const companyUpdates = getUpdates(formData, profile, SECTION_FIELDS.company);
	const letterUpdates = getUpdates(formData, profile, SECTION_FIELDS.letter);

	const submitSection = (section, updates) => (e) => {
		e.preventDefault();
		if (Object.keys(updates).length === 0) return;
		setSavingSection(section);
		saveMutation.mutate({ updates }, { onSettled: () => setSavingSection(null) });
	};

	if (isLoading || error) {
		return (
			<SectionCard icon={BsBuilding} title="Firmendaten" subtitle="Zentrale Firmendaten für Ihre Kommunikation">
				{isLoading ? (
					<div className="flex items-center justify-center py-12">
						<div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
					</div>
				) : (
					<Alert tone="warning" title="Firmendaten konnten nicht geladen werden">
						{error.message}
					</Alert>
				)}
			</SectionCard>
		);
	}

	return (
		<div className="space-y-5">
			<SectionCard icon={BsBuilding} title="Firmendaten" subtitle="Zentrale Firmendaten für Ihre Kommunikation">
				<form onSubmit={submitSection('company', companyUpdates)}>
					<div className="grid gap-4 md:grid-cols-2">
						<FormField label="Firmenname" htmlFor="company-name">
							<TextInput
								id="company-name"
								name="name"
								value={formData.name}
								onChange={handleInput}
								placeholder="Muster GmbH"
							/>
						</FormField>
						<FormField label="E-Mail" htmlFor="company-email">
							<TextInput
								id="company-email"
								type="email"
								name="email"
								value={formData.email}
								onChange={handleInput}
								placeholder="info@beispiel.de"
							/>
						</FormField>
						<FormField label="Straße und Hausnummer" htmlFor="company-street">
							<TextInput
								id="company-street"
								name="street"
								value={formData.street}
								onChange={handleInput}
								placeholder="Musterstraße 1"
							/>
						</FormField>
						<div className="grid grid-cols-[120px_1fr] gap-4">
							<FormField label="PLZ" htmlFor="company-postal">
								<TextInput
									id="company-postal"
									name="postal"
									value={formData.postal}
									onChange={handleInput}
									placeholder="10115"
								/>
							</FormField>
							<FormField label="Ort" htmlFor="company-city">
								<TextInput
									id="company-city"
									name="city"
									value={formData.city}
									onChange={handleInput}
									placeholder="Berlin"
								/>
							</FormField>
						</div>
						<FormField label="Telefon" htmlFor="company-phone">
							<TextInput
								id="company-phone"
								type="tel"
								name="phone"
								value={formData.phone}
								onChange={handleInput}
								placeholder="030 12345678"
							/>
						</FormField>
						<FormField label="Website" htmlFor="company-website">
							<TextInput
								id="company-website"
								type="url"
								name="website"
								value={formData.website}
								onChange={handleInput}
								placeholder="https://www.beispiel.de"
							/>
						</FormField>
					</div>
					<FormFooter
						label="Firmendaten speichern"
						loading={savingSection === 'company'}
						disabled={Object.keys(companyUpdates).length === 0}
					/>
				</form>
			</SectionCard>

			<SectionCard
				icon={BsFileEarmarkText}
				title="Briefvorlagen"
				subtitle="Logo, Registereintrag und Gestaltung für Briefvorlagen und Post-Mailings"
			>
				<form onSubmit={submitSection('letter', letterUpdates)}>
					<div className="grid gap-4 md:grid-cols-2">
						<FormField label="Firmenlogo" className="md:col-span-2">
							<ImageUploadField
								value={formData.logoUrl}
								onChange={(url) => setFormData((prev) => ({ ...prev, logoUrl: url }))}
								hint="Wird in Briefvorlagen und Post-Mailings verwendet."
							/>
						</FormField>
						<FormField label="Handelsregistereintrag" htmlFor="company-registration">
							<TextInput
								id="company-registration"
								name="registration"
								value={formData.registration}
								onChange={handleInput}
								placeholder="HRB 12345 · Amtsgericht Berlin"
							/>
						</FormField>
						<FormField label="Markenfarbe" htmlFor="company-brandPrimaryColor">
							<div className="flex items-center gap-3">
								<input
									type="color"
									name="brandPrimaryColor"
									aria-label="Markenfarbe wählen"
									value={formData.brandPrimaryColor || DEFAULT_BRAND_COLOR}
									onChange={handleInput}
									className="h-10 w-10 flex-shrink-0 cursor-pointer rounded-card border border-bgBase-500 bg-white p-0.5"
								/>
								<TextInput
									id="company-brandPrimaryColor"
									name="brandPrimaryColor"
									value={formData.brandPrimaryColor}
									onChange={handleInput}
									placeholder={DEFAULT_BRAND_COLOR}
									className="max-w-[140px]"
								/>
								{formData.brandPrimaryColor && (
									<Button
										variant="outline"
										size="sm"
										onClick={() => setFormData((prev) => ({ ...prev, brandPrimaryColor: '' }))}
									>
										Zurücksetzen
									</Button>
								)}
							</div>
						</FormField>
						<FormField
							label="Standard-Fußzeile"
							htmlFor="company-defaultFooterText"
							hint="Wird automatisch in neue Briefvorlagen übernommen (z. B. Telefon, Website, HRB)."
							className="md:col-span-2"
						>
							<Textarea
								id="company-defaultFooterText"
								name="defaultFooterText"
								value={formData.defaultFooterText}
								onChange={handleInput}
								rows={3}
								maxLength={500}
								placeholder={'Tel. 030 12345678\nwww.beispiel.de\nHRB 12345 · AG Berlin'}
								className="resize-none"
							/>
						</FormField>
					</div>
					<FormFooter
						label="Briefvorlagen speichern"
						loading={savingSection === 'letter'}
						disabled={Object.keys(letterUpdates).length === 0}
					/>
				</form>
			</SectionCard>
		</div>
	);
};
